import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faUserTie,
    faStore,
    faArrowRight,
    faCheckCircle,
    faCrown
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function UsuariosIndex() {
    const userTypes = [
        {
            id: 'dueño',
            title: 'Dueño de la Marca',
            icon: faCrown,
            description: 'Usuario principal con acceso completo a todas las funcionalidades de la plataforma',
            path: '/documentation/usuarios/dueño',
            status: 'disponible',
            features: [
                'Gestión completa de marcas y tiendas',
                'Gestión de clientes y puntos',
                'Gestión de productos e inventario',
                'Importación y análisis de ventas',
                'Creación y gestión de ofertas',
                'Sistema de WinBack y recuperación de clientes',
                'Gestión de recompensas y premios',
                'Email Marketing y campañas',
                'Generación de infografías',
                'Acceso completo a Analytics y reportes',
                'Gestión de encuestas y alertas',
                'Asignación de trabajadores y franquiciados'
            ],
            color: 'yellow'
        },
        {
            id: 'trabajador',
            title: 'Trabajador',
            icon: faUserTie,
            description: 'Personal de tienda con acceso limitado para gestionar clientes y operaciones diarias',
            path: '/documentation/usuarios/trabajador',
            status: 'disponible',
            features: [
                'Ver tiendas asignadas',
                'Gestionar clientes de las tiendas',
                'Acumular puntos a clientes',
                'Canjear premios y recompensas',
                'Crear nuevos clientes',
                'Enviar códigos de campañas activas',
                'Acceso limitado solo a tiendas asignadas'
            ],
            color: 'blue'
        },
        {
            id: 'franquiciado',
            title: 'Franquiciado',
            icon: faStore,
            description: 'Dueño de franquicia con acceso a estadísticas y gestión operativa de sus tiendas',
            path: '/documentation/usuarios/franquiciado',
            status: 'disponible',
            features: [
                'Ver tiendas asignadas',
                'Gestionar clientes de las tiendas',
                'Acumular puntos a clientes',
                'Canjear premios y recompensas',
                'Crear nuevos clientes',
                'Ver estadísticas y métricas de tiendas',
                'Acceso a reportes de rendimiento',
                'Enviar códigos de campañas activas'
            ],
            color: 'green'
        }
    ];

    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-6">
                        <FontAwesomeIcon icon={faUser} className="text-indigo-600 text-4xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Tipos de Usuarios
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        PetitPrime ofrece diferentes tipos de usuarios según el rol y las responsabilidades dentro de tu negocio. Cada tipo tiene acceso a funcionalidades específicas diseñadas para su función.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                        ¿Qué son los Tipos de Usuarios?
                    </h2>
                    <p className="text-gray-700 mb-4">
                        El sistema de PetitPrime está diseñado para diferentes roles dentro de tu organización:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li><strong>Dueño de la Marca:</strong> Tienes control total sobre tu negocio, desde la gestión de marcas hasta el análisis de datos y la configuración de campañas de marketing.</li>
                        <li><strong>Trabajador:</strong> Personal operativo que gestiona el día a día en las tiendas, atendiendo clientes y gestionando puntos y premios.</li>
                        <li><strong>Franquiciado:</strong> Dueños de franquicias que necesitan ver el rendimiento de sus tiendas además de gestionar operaciones diarias.</li>
                    </ul>
                </div>

                {/* Comparación de Roles */}
                <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">
                        Comparación de Roles
                    </h2>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Funcionalidad
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Dueño
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Trabajador
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Franquiciado
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Gestión de Marcas y Tiendas</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                    </tr>
                                    <tr className="bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Gestión de Clientes</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Acumular Puntos</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr className="bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Canjear Premios</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Ver Estadísticas</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                    </tr>
                                    <tr className="bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Marketing y Campañas</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Gestión de Productos</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                    </tr>
                                    <tr className="bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Asignar Trabajadores/Franquiciados</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><FontAwesomeIcon icon={faCheckCircle} className="text-green-500" /></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center"><span className="text-gray-400">—</span></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Tipos de Usuarios */}
                <div className="space-y-6 mb-8">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">
                        Tipos de Usuarios Disponibles
                    </h2>
                    {userTypes.map((userType) => (
                        <div
                            key={userType.id}
                            className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                        >
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start space-x-4 flex-1">
                                        <div className="flex-shrink-0">
                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                                userType.color === 'yellow' ? 'bg-yellow-100' :
                                                userType.color === 'blue' ? 'bg-blue-100' :
                                                'bg-green-100'
                                            }`}>
                                                <FontAwesomeIcon 
                                                    icon={userType.icon} 
                                                    className={`text-xl ${
                                                        userType.color === 'yellow' ? 'text-yellow-600' :
                                                        userType.color === 'blue' ? 'text-blue-600' :
                                                        'text-green-600'
                                                    }`} 
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-xl font-semibold text-gray-800">
                                                    {userType.title}
                                                </h3>
                                                {userType.status === 'disponible' && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                                        Disponible
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-600 mb-4">
                                                {userType.description}
                                            </p>
                                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                                                {userType.features.map((feature, index) => (
                                                    <li key={index} className="flex items-start text-sm text-gray-700">
                                                        <span className={`mr-2 ${
                                                            userType.color === 'yellow' ? 'text-yellow-500' :
                                                            userType.color === 'blue' ? 'text-blue-500' :
                                                            'text-green-500'
                                                        }`}>•</span>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                {userType.status === 'disponible' && (
                                    <Link
                                        to={userType.path}
                                        className={`inline-flex items-center font-medium transition-colors ${
                                            userType.color === 'yellow' ? 'text-yellow-600 hover:text-yellow-800' :
                                            userType.color === 'blue' ? 'text-blue-600 hover:text-blue-800' :
                                            'text-green-600 hover:text-green-800'
                                        }`}
                                    >
                                        Ver documentación completa
                                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tips */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        💡 Consejos sobre Tipos de Usuarios
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Como dueño de la marca, puedes asignar trabajadores y franquiciados a tus tiendas desde la gestión de tiendas</li>
                        <li>Los trabajadores son ideales para personal operativo que necesita gestionar clientes y puntos en el día a día</li>
                        <li>Los franquiciados son perfectos para dueños de franquicias que necesitan ver el rendimiento de sus tiendas además de gestionar operaciones</li>
                        <li>Cada tipo de usuario tiene su propio login y dashboard personalizado</li>
                        <li>Los permisos están configurados automáticamente según el tipo de usuario</li>
                    </ul>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default UsuariosIndex;

